import { useTranslation } from 'react-i18next';
import { Settings } from 'iconoir-react';
import { useTheme } from 'components/core/theme-provider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { cn } from 'lib/utils';

type SettingsButtonProps = {
  onClick: () => void;
  isActive?: boolean;
  label?: string;
  className?: string;
};

/** Round gear button for headers. Filled while the settings it opens are showing. */
export function SettingsButton({ onClick, isActive = false, label, className }: SettingsButtonProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const buttonLabel = label ?? t('SETTINGS', { defaultValue: 'Settings' });

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            aria-label={buttonLabel}
            aria-haspopup="dialog"
            aria-expanded={isActive}
            className={cn(
              'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border-[0.5px] p-0 transition-opacity hover:opacity-80 2xl:h-8 2xl:w-8 2xl:p-1.5',
              isActive
                ? isDark
                  ? 'border-grey-200 bg-true-white text-black-base'
                  : 'border-grey-200 bg-grey-950 text-true-white'
                : isDark
                  ? 'border-border bg-transparent text-true-white'
                  : 'border-grey-200 bg-true-white text-grey-500',
              className
            )}
          >
            <Settings
              width={20}
              height={20}
              strokeWidth={1.5}
              className="size-[18px] 2xl:size-5"
            />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{buttonLabel}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
